// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent, MouseEvent, ReactNode } from 'react';

import { cx } from './cx';
import { IconButton, type IconButtonSize, type IconButtonVariant } from './IconButton';
import { openIndex } from './menuKeys';
import styles from './Menu.module.css';
import { moveEnabledIndex } from './segmented';

export interface MenuItem {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  onSelect?: () => void;
  /** Renders an <a role="menuitem"> instead (a download). */
  href?: string;
  download?: boolean;
  disabled?: boolean;
  title?: string;
}

export interface MenuProps {
  /** Accessible name of the trigger and the list ("Flere handlinger"). */
  label: string;
  items: readonly MenuItem[];
  /** Trigger icon; default ⋯ */
  icon?: ReactNode;
  /** Default `filled`. */
  variant?: IconButtonVariant;
  /** Default `md`. */
  size?: IconButtonSize;
  /** The list's edge aligns to the trigger's; default `end`. */
  align?: 'start' | 'end';
  className?: string;
}

const LIST_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End']);

/**
 * An icon button that opens a short list of actions (a toolbar's ⋯). The list
 * positions in place under the trigger, above the table's sticky header.
 * Arrows, Home and End move between the enabled items; Escape closes and
 * returns focus to the trigger; Tab or a click elsewhere closes. A disabled
 * item keeps its tooltip (`aria-disabled`, not `disabled`).
 */
export function Menu({ label, items, icon, variant = 'filled', size = 'md', align = 'end', className }: MenuProps) {
  const [open, setOpen] = useState(false);
  // The item to focus once the list renders; -1 leaves focus on the trigger (a click).
  const [focusAt, setFocusAt] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const menuId = useId();
  const skip = (i: number) => !!items[i].disabled;
  const hasIcons = items.some((it) => it.icon != null && it.icon !== false);

  const close = (refocus: boolean) => {
    setOpen(false);
    setFocusAt(-1);
    if (refocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (open && focusAt >= 0) itemRefs.current[focusAt]?.focus();
  }, [open, focusAt]);

  // Escape or a click elsewhere closes it; Escape returns focus to the trigger.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      close(true);
    };
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open]);

  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (open && e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    }
    // ↑/↓ only: Enter and Space click the trigger natively (onClick opens).
    if (openIndex(e.key, items.length) < 0) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
    setFocusAt(moveEnabledIndex(-1, e.key, items.length, skip));
  };

  const onTriggerClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (open) {
      close(false);
      return;
    }
    setOpen(true);
    // A keyboard click (Enter/Space, detail 0) enters the list; a pointer click leaves focus on the trigger.
    if (e.detail === 0) setFocusAt(moveEnabledIndex(-1, 'ArrowDown', items.length, skip));
  };

  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      close(false);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(true);
      return;
    }
    // A link item answers Space too, as a button item does.
    if (e.key === ' ' && e.target instanceof HTMLAnchorElement) {
      e.preventDefault();
      e.stopPropagation();
      e.target.click();
      return;
    }
    // A vertical list: ←/→ stay the shell's.
    if (!LIST_KEYS.has(e.key)) return;
    const focused = itemRefs.current.findIndex((el) => el !== null && el === e.target);
    const to = moveEnabledIndex(focused, e.key, items.length, skip);
    if (to < 0) return;
    e.preventDefault();
    e.stopPropagation();
    itemRefs.current[to]?.focus();
  };

  const onBlur = (e: FocusEvent<HTMLDivElement>) => {
    // Focus moved somewhere else on the page (not to nowhere — a click on the list's padding).
    if (open && e.relatedTarget instanceof Node && !rootRef.current?.contains(e.relatedTarget)) close(false);
  };

  const activate = (item: MenuItem) => {
    // A link closes a tick later, so the <a> is still mounted for the browser's default action (the download).
    if (item.href !== undefined) window.setTimeout(() => close(true), 0);
    else close(true);
    item.onSelect?.();
  };

  return (
    <div ref={rootRef} className={cx(styles.root, className)} onBlur={onBlur}>
      <IconButton
        ref={triggerRef}
        variant={variant}
        size={size}
        label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={onTriggerClick}
        onKeyDown={onTriggerKey}
      >
        {icon ?? <MoreIcon />}
      </IconButton>
      {open && (
        <div id={menuId} className={cx(styles.menu, styles[align])} role="menu" aria-label={label} onKeyDown={onListKey}>
          {items.map((item, i) => {
            const body = (
              <>
                {/* Once any item has an icon, every item keeps the slot, so the labels line up. */}
                {hasIcons && (
                  <span className={styles.icon} aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                <span>{item.label}</span>
              </>
            );
            const ref = (el: HTMLElement | null) => {
              itemRefs.current[i] = el;
            };
            if (item.href !== undefined) {
              return (
                <a
                  key={item.id}
                  ref={ref}
                  role="menuitem"
                  tabIndex={-1}
                  className={styles.item}
                  href={item.href}
                  download={item.download || undefined}
                  title={item.title}
                  aria-disabled={item.disabled || undefined}
                  onClick={(e) => {
                    if (item.disabled) {
                      e.preventDefault();
                      return;
                    }
                    // The link still follows (the download); the list then closes.
                    activate(item);
                  }}
                >
                  {body}
                </a>
              );
            }
            return (
              <button
                key={item.id}
                ref={ref}
                type="button"
                role="menuitem"
                tabIndex={-1}
                className={styles.item}
                title={item.title}
                aria-disabled={item.disabled || undefined}
                onClick={() => {
                  if (!item.disabled) activate(item);
                }}
              >
                {body}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** The ⋯ of a "more actions" trigger: three 16px dots. */
export function MoreIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" focusable="false">
      <circle cx="3.5" cy="8" r="1.25" />
      <circle cx="8" cy="8" r="1.25" />
      <circle cx="12.5" cy="8" r="1.25" />
    </svg>
  );
}

/** A download item's icon (Eksportér CSV): a 16px arrow down onto a tray. */
export function DownloadIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M8 2.5v7M5 6.75l3 3 3-3M3 10.5v2h10v-2" />
    </svg>
  );
}
